import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Checkbox,
  Typography,
  Box,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  useGetPhysiciansByCompanyQuery,
  useGetCentersQuery,
  useAssignPhysicianToGroupMutation,
} from "../../../../services/ConfigurationService";
import { PhysicianType } from "../../../../services/ConfigurationService/types/physicianType";

interface AssignPhysicianDialogProps {
  open: boolean;
  onClose: () => void;
  groupId: string;
  company: string;
}

const AssignPhysicianDialog: React.FC<AssignPhysicianDialogProps> = ({ open, onClose, groupId, company }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { data: physicians = [] } = useGetPhysiciansByCompanyQuery({ company }, { skip: !open });
  const { data: centers = [] } = useGetCentersQuery({ company }, { skip: !open });
  const [assignPhysician, { isLoading }] = useAssignPhysicianToGroupMutation();

  const centerMap = useMemo(() => {
    const map = new Map<string, string>();
    centers.forEach((c) => map.set(c.id, c.shortName || c.name));
    return map;
  }, [centers]);

  const availablePhysicians = useMemo(() => {
    const filtered = (physicians as PhysicianType[]).filter(
      (p) => !p.physicianGroupId || p.physicianGroupId !== groupId
    );
    if (!searchTerm.trim()) return filtered;
    const lower = searchTerm.toLowerCase();
    return filtered.filter(
      (p) =>
        p.code?.toLowerCase().includes(lower) ||
        p.firstName?.toLowerCase().includes(lower) ||
        p.lastName?.toLowerCase().includes(lower)
    );
  }, [physicians, searchTerm, groupId]);

  const handleToggle = (id: string) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));
  };

  const handleAssign = async () => {
    const errors: string[] = [];
    for (const physicianId of selected) {
      try {
        await assignPhysician({ physicianId, physicianGroupId: groupId }).unwrap();
      } catch (err) {
        errors.push(physicianId);
      }
    }
    if (errors.length > 0) {
      setErrorMessage(`Failed to assign ${errors.length} physician(s). They may have been deleted or already assigned.`);
    }
    setSelected([]);
    setSearchTerm("");
    onClose();
  };

  const handleCancel = () => {
    setSelected([]);
    setSearchTerm("");
    onClose();
  };

  return (
    <>
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Assign Physicians to Group</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by name or code..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 2, mt: 1 }}
        />
        {availablePhysicians.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              No unassigned physicians found.
            </Typography>
          </Box>
        ) : (
          <List dense sx={{ maxHeight: 350, overflow: "auto" }}>
            {availablePhysicians.slice(0, 50).map((p) => (
              <ListItem key={p.id} disablePadding>
                <ListItemButton onClick={() => handleToggle(p.id)} dense>
                  <Checkbox
                    edge="start"
                    checked={selected.includes(p.id)}
                    tabIndex={-1}
                    disableRipple
                  />
                  <ListItemText
                    primary={`${p.lastName}, ${p.firstName}`}
                    secondary={`Code: ${p.code} | Center: ${centerMap.get(p.centerId) || p.centerId}`}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleAssign}
          disabled={isLoading || selected.length === 0}
        >
          Assign ({selected.length})
        </Button>
      </DialogActions>
    </Dialog>
    <Snackbar
      open={!!errorMessage}
      autoHideDuration={6000}
      onClose={() => setErrorMessage(null)}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    >
      <Alert severity="error" onClose={() => setErrorMessage(null)}>
        {errorMessage}
      </Alert>
    </Snackbar>
    </>
  );
};

export { AssignPhysicianDialog };
